import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getIntegrationReferenceData, getIntegrationRuns, supportContextFromError } from '../../lib/services/integrations'

export function IntegrationRunsPage() {
  const runsQuery = useQuery({
    queryKey: ['integrations-runs'],
    queryFn: () => getIntegrationRuns(),
  })

  const dataQuery = useQuery({
    queryKey: ['integrations-reference-data'],
    queryFn: () => getIntegrationReferenceData(),
  })

  const runs = runsQuery.data ?? []
  const items = dataQuery.data ?? []

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Integration Run History</h1>
        <p className="mt-1 text-sm text-slate-600">Run-level history and latest read-only ingestion output records.</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Runs</h2>
        {runsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading run history...</p>}
        {runsQuery.error && (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <p className="text-sm font-semibold">Failed to load run history</p>
            <p className="text-xs">{(runsQuery.error as Error).message}</p>
            <p className="mt-1 text-xs text-red-600">Support context: {supportContextFromError(runsQuery.error)}</p>
          </div>
        )}
        {!runsQuery.isLoading && !runsQuery.error && runs.length === 0 && <p className="mt-2 text-sm text-slate-500">No runs found.</p>}
        <div className="mt-3 grid gap-2">
          {runs.map((run) => (
            <div key={run.ingestion_timestamp} className="v11-dashboard-card px-3 py-2 text-sm text-slate-700">
              <span className="font-semibold">{run.ingestion_timestamp}</span> | Items: {run.item_count}
            </div>
          ))}
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Latest Records (Read-Only)</h2>
        {dataQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading records...</p>}
        {dataQuery.error && (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <p className="text-sm font-semibold">Failed to load records</p>
            <p className="text-xs">{(dataQuery.error as Error).message}</p>
            <p className="mt-1 text-xs text-red-600">Support context: {supportContextFromError(dataQuery.error)}</p>
          </div>
        )}
        {!dataQuery.isLoading && !dataQuery.error && items.length === 0 && <p className="mt-2 text-sm text-slate-500">No records found.</p>}
        <div className="mt-3 grid gap-2">
          {items.map((item) => (
            <div key={`${item.ingestion_timestamp}-${item.external_record_id}`} className="v11-dashboard-card px-3 py-2 text-sm text-slate-700">
              <p className="font-semibold">{item.external_record_id}</p>
              <p className="text-xs text-slate-500">{item.ingestion_timestamp}</p>
            </div>
          ))}
        </div>
      </section>

      <Link to="/integrations/status" className="inline-block text-sm underline">
        Back to integrations status
      </Link>
    </div>
  )
}
